import { getApiData, postFormData } from "../utils/APIHelper";

/**
 * Issue-log endpoints live on the v2 API (Setting.newBaseUrl). Use isAlternateUrl=true on getApiData.
 * Spring-style pagination returns { content, totalPages, totalElements, first, last, number, size }.
 */

const ISSUE_LOG_ENDPOINTS = {
    LIST: (projectId, params) => {
        const qs = new URLSearchParams();
        Object.keys(params || {}).forEach((k) => {
            if (params[k] !== undefined && params[k] !== null && params[k] !== "") {
                qs.append(k, params[k]);
            }
        });
        return `api/v2/issue-logs/project/${projectId}?${qs.toString()}`;
    },
    DETAIL: (id) => `api/v2/issue-logs/${id}`,
    UPDATE: (id) => `api/v2/issue-logs/${id}`,
    RESOLVE: (id) => `api/v2/issue-logs/${id}/resolve`,
    WITH_IMAGE: `api/v2/issue-logs/with-image`,
    ACTIVITY_TYPES: `api/v2/issue-categories/activity-types`,
    CATEGORIES_BY_TYPE: (activityType) =>
        `api/v2/issue-categories/activity-type/${encodeURIComponent(activityType)}`,
    ANALYTICS: (projectId) => `api/v2/issue-logs/project/${projectId}/analytics`,
};

/**
 * GET /api/v2/issue-logs/project/:projectId/analytics
 * Returns { total_issues, total_open, total_in_progress, total_resolved }
 */
export const getIssueAnalytics = async (projectId) => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.ANALYTICS(projectId),
            "GET",
            {},
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error fetching issue analytics:", error);
        throw error;
    }
};

/**
 * Paginated rooms list for the logged-in project.
 * GET /api/project/:projectId/rooms?page=&search=
 * Returns { success, data, page, total_pages, next_enable, prev_enable, ... }
 */
export const listProjectRooms = async (projectId, { page = 1, search = "" } = {}) => {
    const params = new URLSearchParams({ page: String(page) });
    const q = (search || "").trim();
    if (q.length >= 3) params.set("search", q);
    try {
        return await getApiData(
            `api/project/${projectId}/rooms?${params.toString()}`,
            "GET",
            {},
            { "Content-Type": "application/json" }
        );
    } catch (error) {
        console.error("Error fetching project rooms:", error);
        throw error;
    }
};

export const listIssueLogs = async (
    projectId,
    { page = 0, size = 10, sortBy = "date", sortDirection = "DESC", status, sourceType } = {}
) => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.LIST(projectId, {
                page,
                size,
                sortBy,
                sortDirection,
                status,
                sourceType,
            }),
            "GET",
            {},
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error listing issue logs:", error);
        throw error;
    }
};

export const getIssueLogDetail = async (id) => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.DETAIL(id),
            "GET",
            {},
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error fetching issue log detail:", error);
        throw error;
    }
};

export const updateIssueLog = async (id, body) => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.UPDATE(id),
            "PUT",
            body,
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error updating issue log:", error);
        throw error;
    }
};

/**
 * Resolve an issue — multipart with comments, resolvedBy, image.
 */
export const resolveIssueLog = async (id, { comments, resolvedBy, image }) => {
    try {
        const fd = new FormData();
        fd.append("comments", comments || "");
        fd.append("resolvedBy", String(resolvedBy ?? ""));
        if (image) fd.append("image", image);
        return await postFormData(ISSUE_LOG_ENDPOINTS.RESOLVE(id), fd, {}, true);
    } catch (error) {
        console.error("Error resolving issue log:", error);
        throw error;
    }
};

/**
 * Create a new issue log with an image (multipart: image, issueLog JSON string).
 */
export const createIssueLogWithImage = async ({ image, issueLog }) => {
    try {
        const fd = new FormData();
        if (image) fd.append("image", image);
        fd.append(
            "issueLog",
            typeof issueLog === "string" ? issueLog : JSON.stringify(issueLog)
        );
        return await postFormData(ISSUE_LOG_ENDPOINTS.WITH_IMAGE, fd, {}, true);
    } catch (error) {
        console.error("Error creating issue log with image:", error);
        throw error;
    }
};

export const getIssueCategoryActivityTypes = async () => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.ACTIVITY_TYPES,
            "GET",
            {},
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error fetching issue category activity types:", error);
        throw error;
    }
};

export const getIssueCategoriesByActivityType = async (activityType) => {
    try {
        return await getApiData(
            ISSUE_LOG_ENDPOINTS.CATEGORIES_BY_TYPE(activityType),
            "GET",
            {},
            { "Content-Type": "application/json" },
            true
        );
    } catch (error) {
        console.error("Error fetching issue categories by activity type:", error);
        throw error;
    }
};

export const SOURCE_TYPE = {
    DAILY_UPDATE: "DAILY_UPDATE",
    INSPECTION: "INSPECTION",
    SITE_VISIT: "SITE_VISIT",
    CUSTOMER: "CUSTOMER",
};

export const ISSUE_STATUS = {
    OPEN: "OPEN",
    IN_PROGRESS: "IN_PROGRESS",
    DONE: "DONE",
    RESOLVED: "RESOLVED",
};

export const SEVERITY = {
    CRITICAL: "CRITICAL",
    HIGH: "HIGH",
    MEDIUM: "MEDIUM",
    LOW: "LOW",
};

export const formatEnumForDisplay = (val) => {
    if (!val) return "";
    return String(val)
        .replace(/[_-]/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const getStatusStyles = (status) => {
    switch (status) {
        case ISSUE_STATUS.OPEN:
            return { backgroundColor: "#FEF3C7", color: "#92400E" };
        case ISSUE_STATUS.IN_PROGRESS:
            return { backgroundColor: "#DBEAFE", color: "#1E40AF" };
        case ISSUE_STATUS.DONE:
            return { backgroundColor: "#D1FAE5", color: "#065F46" };
        case ISSUE_STATUS.RESOLVED:
            return { backgroundColor: "#E5E7EB", color: "#374151" };
        default:
            return { backgroundColor: "#F3F4F6", color: "#374151" };
    }
};

export const getPriorityStyles = (severity) => {
    switch (severity) {
        case SEVERITY.CRITICAL:
            return { backgroundColor: "#FEE2E2", color: "#991B1B" };
        case SEVERITY.HIGH:
            return { backgroundColor: "#FED7AA", color: "#9A3412" };
        case SEVERITY.MEDIUM:
            return { backgroundColor: "#FEF3C7", color: "#92400E" };
        case SEVERITY.LOW:
            return { backgroundColor: "#DBEAFE", color: "#1E40AF" };
        default:
            return { backgroundColor: "#F3F4F6", color: "#374151" };
    }
};

export const getMarkupFromMetadata = (metadata) => {
    if (!metadata) return 0;
    try {
        const obj = typeof metadata === "string" ? JSON.parse(metadata) : metadata;
        return Number(obj?.markup ?? 0);
    } catch {
        return 0;
    }
};
